/**
 * vendorBanksApi — RTK Query slice cho /vendors/{code}/banks/*.
 * Tag enhanced: 'VendorBank'. Composite key = (cp_code, bank, bank_account).
 *
 * Endpoints (Sprint 5):
 *   GET    /vendors/{code}/banks/list
 *   POST   /vendors/{code}/banks/create
 *   PUT    /vendors/{code}/banks/{bank_account}?bank=...
 *   DELETE /vendors/{code}/banks/{bank_account}?bank=...
 */
import { baseApi } from './baseApi'
import type {
  CpBankCreateIn,
  CpBankOut,
  CpBankUpdateIn,
  DataResponse,
  MessageResponse,
} from '@/types/api'

const vendorBanksEnhanced = baseApi.enhanceEndpoints({
  addTagTypes: ['VendorBank'],
})

export const vendorBanksApi = vendorBanksEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listVendorBanks: b.query<DataResponse<CpBankOut[]>, { code: string }>({
      query: ({ code }) => `/vendors/${encodeURIComponent(code)}/banks/list`,
      providesTags: (_r, _e, arg) => [
        { type: 'VendorBank', id: `LIST-${arg.code}` },
      ],
    }),
    createVendorBank: b.mutation<
      DataResponse<CpBankOut>,
      { code: string; body: CpBankCreateIn }
    >({
      query: ({ code, body }) => ({
        url: `/vendors/${encodeURIComponent(code)}/banks/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'VendorBank', id: `LIST-${arg.code}` },
      ],
    }),
    updateVendorBank: b.mutation<
      DataResponse<CpBankOut>,
      { code: string; bank: string; bank_account: string; body: CpBankUpdateIn }
    >({
      query: ({ code, bank, bank_account, body }) => ({
        url: `/vendors/${encodeURIComponent(code)}/banks/${encodeURIComponent(bank_account)}`,
        method: 'PUT',
        params: { bank },
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'VendorBank', id: `LIST-${arg.code}` },
      ],
    }),
    deleteVendorBank: b.mutation<
      MessageResponse,
      { code: string; bank: string; bank_account: string }
    >({
      query: ({ code, bank, bank_account }) => ({
        url: `/vendors/${encodeURIComponent(code)}/banks/${encodeURIComponent(bank_account)}`,
        method: 'DELETE',
        params: { bank },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'VendorBank', id: `LIST-${arg.code}` },
      ],
    }),
  }),
})

export const {
  useListVendorBanksQuery,
  useCreateVendorBankMutation,
  useUpdateVendorBankMutation,
  useDeleteVendorBankMutation,
} = vendorBanksApi
